import React, { useState, useRef, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { FiSend, FiChevronDown, FiMessageCircle } from "react-icons/fi";
import { FaRobot, FaMicrophone } from "react-icons/fa";
import { FaStop } from "react-icons/fa6";
import { IoMdClose } from "react-icons/io";
import { HiPlus } from "react-icons/hi";
import { BsArrowsExpand } from "react-icons/bs";
import { 
  AiOutlineFile,
  AiOutlineFileImage,
  AiOutlineFilePdf,
  AiOutlineFileText,
  AiOutlineFileSearch,
} from "react-icons/ai";
import { ImSpinner8 } from "react-icons/im";
import styles from "./ChatInput.module.css";
import { AssistantContext, DEFAULT_LLMS } from "../../context/AssistantContext";
import useChatSSE from "../../hooks/useChatSSE";
import useVoiceRecorder from "../../hooks/useVoiceRecorder"; 

const ChatInput = () => {
  const [message, setMessage] = useState("");
  const { settings, updateSettings } = useContext(AssistantContext);
  const [mode, setMode] = useState(settings.mode || 'chat');
  const [modeDropdownOpen, setModeDropdownOpen] = useState(false);
  const modes = ["agent", "assistant", "chat"];
  const modeDescriptions = {
    chat: (
      <>
        Fast responses from various LLMs for general questions and document/image analysis. Choose from different LLMs for speed or capability.
        <br />
        <br />
        <span style={{ color: '#fbbf24', fontWeight: 'bold' }}>⚠️ Note:</span>
        {" Answers are limited to LLM's training data and could include inaccuracies or hallucinations. Best for general questions, document processing, code generation, etc."}
      </>
    ),
    agent: (
      <>
        Uses advanced reasoning models and specialized tools. Provides well-researched, comprehensive answers with higher accuracy and depth.
        <br />
        <br />
        <span style={{ color: '#10b981', fontWeight: 'bold' }}>✨ Note: </span>
        {"Slower but more thorough - perfect for complex questions requiring internal knowledge, data analysis, or multi-step research."}
      </>
    ),
    assistant: (
      <>
        Searches only official documentation sources to provide fact-based answers with source links. Prevents AI hallucinations by sticking strictly to verified documentation.
        <br />
        <br />
        <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>📚 Best for:</span>
        {" Verified, accurate information when you need reliable answers from official documentation."}
      </>
    )
  };
  const toggleModeDropdown = () => {
    setModeDropdownOpen(prevOpen => {
      const newOpen = !prevOpen;
      if (newOpen) {
        setToolsDropdownOpen(false);
        setAssistantsDropdownOpen(false);
      }
      return newOpen;
    });
  };
  const selectMode = (selected) => {
    setMode(selected);
    setModeDropdownOpen(false);
    // Close other dropdowns when switching modes
    if (selected !== 'agent' || !settings.agent.showToolsSelection) {
      setToolsDropdownOpen(false);
    }
    if (selected !== 'assistant') {
      setAssistantsDropdownOpen(false);
    } else {
      // prompt user to choose a solution
      setAssistantsDropdownOpen(true);
    }
    // Persist mode change
    updateSettings({ ...settings, mode: selected });
  };
  // Multi-select tools dropdown
  const [selectedTools, setSelectedTools] = useState(settings.agent.selectedTools || []);
  const [toolsDropdownOpen, setToolsDropdownOpen] = useState(false);
  const [assistantsDropdownOpen, setAssistantsDropdownOpen] = useState(false);
  const [llmDropdownOpen, setLlmDropdownOpen] = useState(false);

  // Multi-select assistants dropdown
  const [selectedAssistants, setSelectedAssistants] = useState(() => settings.assistant.selectedSolutions || []);
  // Single-select LLMs
  const [selectedLLM, setSelectedLLM] = useState(() => settings.chat.selectedLLM || 'gpt-5.6-sol');
  // LLM list and descriptions - only use frontend-defined LLMs in frontend order
  const llms = DEFAULT_LLMS;
  const llmDescriptions = {
    'gpt-5.6-sol': (
      <>
        <span style={{ fontWeight: 'bold', color: '#8b5cf6' }}>🧠 Deep Reasoning</span>
        <br />
        gpt‑5.6‑sol is the flagship reasoning model in the GPT‑5.6 family, designed for the most complex, compute-intensive workloads. It combines deep reasoning, multi-agent orchestration, advanced coding capabilities, and a 1M-token context window to solve sophisticated problems across analysis, software engineering, research, and agentic workflows.
        <br />
        <br />
        <span style={{ color: '#10b981', fontWeight: 'bold' }}>✨ Best for:</span>
        {" Complex analysis, multi-step reasoning, and tasks requiring deep thinking and logic."}
        <br />
        <br />
        <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>⚡ Note:</span>
        {" Slower but most thorough and accurate for complex tasks."}
      </>
    ),
    'claude-opus-5-5': (
      <>
        <span style={{ fontWeight: 'bold', color: '#d97706' }}>🛠️ Engineering Powerhouse</span>
        <br />
        Claude Opus 5.5 is Anthropic's most advanced Opus model, powering long-running agents while delivering improvements in coding and professional work.
        <br />
        <br />
        <span style={{ color: '#10b981', fontWeight: 'bold' }}>✨ Best for:</span>
        {" Software engineering, deep research, working with slides and spreadsheets, and complex agentic tasks."}
        <br />
        <br />
        <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>⚡ Note:</span>
        {" Best model for coding and real-world software engineering."}
      </>
    ),
    'claude-fable-5': (
      <>
        <span style={{ fontWeight: 'bold', color: '#ec4899' }}>📖 Creative Storyteller</span>
        <br />
        Claude Fable 5 is our next generation of intelligence for the hardest knowledge work and coding problems. It works independently for longer than any prior generally available Claude model: run it in an agent harness and it can work for days at a time, planning across stages, delegating to sub-agents, and checking its own work.
        <br />
        <br />
        <span style={{ color: '#10b981', fontWeight: 'bold' }}>✨ Best for:</span>
        {" Long-running agentic work, complex knowledge tasks, and multi-stage projects requiring sustained autonomy."}
      </>
    )
  };
  const tools = settings.agent.tools || ["docs", "workfront","slack","memory"];
  const toolDescriptions = {
    "docs": "Query the solution documents for the selected assistant.",
    "workfront": "Get data from Workfront.",
    "slack": "Search messages in Slack.",
    "memory": "Search the memories."
  };
  const toolsRef = useRef(null);
  const assistantsRef = useRef(null);
  const llmRef = useRef(null);
  const modeRef = useRef(null);
  const toggleToolsDropdown = () => {
    setToolsDropdownOpen(prevOpen => {
      const newOpen = !prevOpen;
      if (newOpen) {
        setModeDropdownOpen(false);
        setAssistantsDropdownOpen(false);
        setLlmDropdownOpen(false);
      }
      return newOpen;
    });
  };
  const toggleAssistantsDropdown = () => {
    setAssistantsDropdownOpen(prevOpen => {
      const newOpen = !prevOpen;
      if (newOpen) {
        setModeDropdownOpen(false);
        setToolsDropdownOpen(false);
        setLlmDropdownOpen(false);
      }
      return newOpen;
    });
  };
  const toggleLlmDropdown = () => {
    setLlmDropdownOpen(prevOpen => {
      const newOpen = !prevOpen;
      if (newOpen) {
        setModeDropdownOpen(false);
        setToolsDropdownOpen(false);
        setAssistantsDropdownOpen(false);
      }
      return newOpen;
    });
  };
  const toggleToolSelection = (tool) => {
    const newSelected = selectedTools.includes(tool)
      ? selectedTools.filter(item => item !== tool)
      : [...selectedTools, tool];
    
    setSelectedTools(newSelected);
    updateSettings({ 
      ...settings, 
      agent: { ...settings.agent, selectedTools: newSelected } 
    });
  };
  const handleLLMSelect = (llm) => {
    setSelectedLLM(llm);
    setLlmDropdownOpen(false);
    // Persist model for backend endpoint
    if (mode === 'chat') {
      updateSettings({ 
        ...settings, 
        chat: { ...settings.chat, selectedLLM: llm } 
      });
    }
  };
  const assistants = settings.assistant.solutions || [];
  // Map a stored solution value to a friendlier display label.
  // The stored value is what gets sent to the backend (lowercased, spaces -> dashes),
  // so keep it as the backend expects while showing a nicer label in the UI/tooltip.
  const ASSISTANT_DISPLAY_LABELS = {
    "Pass": "Adobe Pass",
    "Audience-Manager": "Audience Manager",
    "KB-Articles": "Knowledge Base Articles",
  };
  const getAssistantDisplay = (full) => ASSISTANT_DISPLAY_LABELS[full] || full;
  const toggleAssistantSelection = (assistant) => {
    const isSelected = selectedAssistants.includes(assistant);
    // Prevent deselecting the last remaining solution
    if (isSelected && selectedAssistants.length === 1) {
      return;
    }
    const newSelected = isSelected
      ? selectedAssistants.filter(item => item !== assistant)
      : [...selectedAssistants, assistant];

    setSelectedAssistants(newSelected);
    updateSettings({
      ...settings,
      assistant: { ...settings.assistant, selectedSolutions: newSelected }
    });
  };

  // Close all dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (toolsDropdownOpen && toolsRef.current && !toolsRef.current.contains(e.target)) {
        setToolsDropdownOpen(false);
      }
      if (assistantsDropdownOpen && assistantsRef.current && !assistantsRef.current.contains(e.target)) {
        setAssistantsDropdownOpen(false);
      }
      if (llmDropdownOpen && llmRef.current && !llmRef.current.contains(e.target)) {
        setLlmDropdownOpen(false);
      }
      if (modeDropdownOpen && modeRef.current && !modeRef.current.contains(e.target)) {
        setModeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [toolsDropdownOpen, assistantsDropdownOpen, llmDropdownOpen, modeDropdownOpen]);

  // Close tools dropdown if Chat mode is selected
  useEffect(() => {
    if (mode !== 'agent') {
      setToolsDropdownOpen(false);
    }
    if (mode !== 'assistant') {
      setAssistantsDropdownOpen(false);
    }
    if (mode !== 'chat') {
      setLlmDropdownOpen(false);
    }
  }, [mode]);

  // Keep local state in sync with settings from context
  useEffect(() => {
    setMode(settings.mode || 'chat');
    setSelectedTools(settings.agent?.selectedTools || []);
    setSelectedAssistants(settings.assistant?.selectedSolutions || []);
    setSelectedLLM(settings.chat?.selectedLLM || 'gpt-5.6-sol');
  }, [settings]);

  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [isResizing, setIsResizing] = useState(false);
  const [hasManuallyResized, setHasManuallyResized] = useState(false);
  const [isTooLarge, setIsTooLarge] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const textareaRef = useRef(null);
  const initialHeightRef = useRef(null);
  const initialClickRef = useRef(null);
  const dragCounter = useRef(0);
  const { sendMessage } = useChatSSE();
  const { conversationId } = useParams();
  const {
    isRecording,
    isTranscribing,
    recordingDuration,
    error: voiceError,
    startRecording,
    stopRecording,
    clearError: clearVoiceError,
  } = useVoiceRecorder();

  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleVoiceClick = async () => {
    if (isRecording) {
      const text = await stopRecording();
      if (text) {
        setMessage(prev => prev ? `${prev} ${text}` : text);
        if (!hasManuallyResized && textareaRef.current) {
          setTimeout(() => {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight - 10}px`;
          }, 0);
        }
      }
    } else {
      clearVoiceError();
      startRecording();
    }
  };

  // Maximum size in bytes (65kb)
  const MAX_MESSAGE_SIZE = 65 * 1000;

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizing && textareaRef.current && initialHeightRef.current !== null && initialClickRef.current !== null) {
        const deltaY = initialClickRef.current - e.clientY;
        const newHeight = initialHeightRef.current + deltaY;
        const maxHeight = window.innerHeight - 250;
        textareaRef.current.style.height = `${Math.min(Math.max(30, newHeight), maxHeight)}px`;
      }
    };

    const handleMouseUp = () => {
      if (isResizing) {
        setHasManuallyResized(true);
      }
      setIsResizing(false);
      initialHeightRef.current = null;
      initialClickRef.current = null;
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Check message size whenever message changes
  useEffect(() => {
    const messageSize = new Blob([JSON.stringify(message)]).size;
    setIsTooLarge(messageSize > MAX_MESSAGE_SIZE);
  }, [message]);

  // Keep local mode in sync with settings if it changes elsewhere
  useEffect(() => {
    if (settings.mode !== mode) {
      setMode(settings.mode);
    }
  }, [settings.mode]);

  const handleResizeStart = (e) => {
    if (textareaRef.current) {
      initialHeightRef.current = textareaRef.current.getBoundingClientRect().height;
      initialClickRef.current = e.clientY;
      setIsResizing(true);
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    switch (ext) {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <AiOutlineFileImage size={16} />;
      case 'pdf':
        return <AiOutlineFilePdf size={16} />;
      case 'txt':
      case 'doc':
      case 'docx':
        return <AiOutlineFileText size={16} />;
      default:
        return <AiOutlineFile size={16} />;
    }
  };

  const truncateFileName = (name) => {
    if (name.length > 25) {
      const ext = name.split('.').pop();
      const truncated = name.substring(0, 20);
      return `${truncated}...${ext}`;
    }
    return name;
  };

  const handleChange = (e) => {
    setMessage(e.target.value);
    
    // Only auto-resize if user hasn't manually resized
    if (!hasManuallyResized && textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight - 10}px`;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (message.trim() && !isTooLarge && assistantSelected) {
      sendMessage(message, uploadedFile, conversationId);
      setMessage("");
      // Use the ref instead of querySelector since we're using CSS modules
      if (textareaRef.current) {
        // Reset manual resize state and height on submit
        setHasManuallyResized(false);
        textareaRef.current.style.height = "30px";
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (!e.shiftKey) {
        e.preventDefault();
        if (message.trim() && !isTooLarge && assistantSelected) {
          sendMessage(message, uploadedFile, conversationId);
          setMessage("");
          // Use the ref instead of e.target since we're using CSS modules
          if (textareaRef.current) {
            // Reset manual resize state and height on send
            setHasManuallyResized(false);
            textareaRef.current.style.height = "30px";
          }
        }
      }
    }
  };

  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedFile(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setFileName("");
  };

  const enableAutoResize = () => {
    setHasManuallyResized(false);
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight - 10}px`;
    }
  };

  // Paste handler for images and files from clipboard
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;

        const name = file.name && file.name !== 'image.png' && file.name !== 'blob'
          ? file.name
          : `pasted-image-${Date.now()}.${file.type.split('/')[1] || 'png'}`;

        setFileName(name);
        const reader = new FileReader();
        reader.onload = (event) => {
          setUploadedFile(event.target.result);
        };
        reader.readAsDataURL(file);
        break;
      }
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0]; // Only take the first file
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedFile(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Determine if a doc assistant has been chosen when in doc assistants mode
  const assistantSelected = mode !== 'assistant' || selectedAssistants.length > 0;

  return (
    <div 
      className={`${styles['chat-input']} ${isDragging ? styles['dragging'] : ''}`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <form onSubmit={handleSubmit} className={styles['chat-form']}>
        {uploadedFile && (
          <div className={styles['attachment-preview']}>
            {getFileIcon(fileName)}
            <span title={fileName}>{truncateFileName(fileName)}</span>
            <button
              type="button"
              className={styles['remove-attachment']}
              onClick={handleRemoveFile}
              aria-label="Remove attachment"
            >
              <IoMdClose size={14} />
            </button>
          </div>
        )}
        {isTooLarge && (
          <div className={styles['size-error-message']}>
            Message exceeds limit. Please reduce the message size.
          </div>
        )}
        {voiceError && (
          <div className={styles['size-error-message']}>
            {voiceError}
            <button
              type="button"
              className={styles['remove-attachment']}
              onClick={clearVoiceError}
              aria-label="Dismiss error"
              style={{ marginLeft: 8, display: 'inline-flex' }}
            >
              <IoMdClose size={14} />
            </button>
          </div>
        )}
        <div className={styles['textarea-container']}>
          <div className={styles['resize-controls']}>
            <div
              className={styles['resize-handle']}
              onMouseDown={handleResizeStart}
              role="presentation"
            />
            <button
              type="button"
              className={`${styles['auto-resize-toggle']} ${hasManuallyResized ? styles.visible : ''}`}
              onClick={enableAutoResize}
              aria-label="Enable auto-resize"
            >
              <BsArrowsExpand size={14} />
            </button>
          </div>
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder="Type a message, or paste / drop files here..."
            className={styles['chat-textarea']}
            rows={1}
          />
        </div>
        <div className={styles['input-actions']}>
          <div className={styles['action-buttons-left']}>
            <label htmlFor="file-upload" className={styles['file-upload']}>
              <HiPlus size={20} />
            </label>
            <input
              id="file-upload"
              type="file"
              style={{ display: "none" }}
              onChange={handleUpload}
            />
            <div className={styles['mode-dropdown']} ref={modeRef}>
              <button
                type="button"
                className={styles['mode-button']}
                onClick={toggleModeDropdown}
                data-tooltip={modeDropdownOpen ? undefined : "Select interaction mode"}
              >
                {mode === 'chat' && <FiMessageCircle size={15} />}
                {mode === 'agent' && <FaRobot size={15} />}
                {mode === 'assistant' && <AiOutlineFileSearch size={15} />}
                {mode === 'assistant' ? 'Doc Assistant' : mode.charAt(0).toUpperCase() + mode.slice(1)}{' '}
                <FiChevronDown
                  size={14}
                  className={!modeDropdownOpen ? styles['rotated'] : ''}
                />
              </button>
              {modeDropdownOpen && (
                <ul className={styles['dropdown-menu']}>
                  {modes.map(item => {
                    let icon = null;
                    if (item === 'chat') icon = <FiMessageCircle size={18} style={{ marginRight: 8 }} />;
                    if (item === 'agent') icon = <FaRobot size={18} style={{ marginRight: 8 }} />;
                    if (item === 'assistant') icon = <AiOutlineFileSearch size={18} style={{ marginRight: 8 }} />;
                    return (
                      <li
                        key={item}
                        className={
                          mode === item
                            ? `${styles['dropdown-menu-item']} ${styles.selected}`
                            : styles['dropdown-menu-item']
                        }
                        onClick={() => selectMode(item)}
                      >
                        {icon}
                        <span className={styles['menu-item-text']}>
                        {item === 'assistant' ? 'Doc Assistant' : item.charAt(0).toUpperCase() + item.slice(1)}
                        </span>
                        <div className={styles.tooltip}>
                          {modeDescriptions[item]}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            {mode === 'agent' && settings.agent.showToolsSelection && (
              <div className={styles['tools-dropdown']} ref={toolsRef}>
                <button
                  type="button"
                  className={styles['tools-button']}
                  onClick={toggleToolsDropdown}
                  data-tooltip={toolsDropdownOpen ? undefined : "Select agent tools"}
                >
                  {selectedTools.length === 0
                    ? 'Tools'
                    : selectedTools.length === 1
                      ? selectedTools[0]
                      : `${selectedTools.length} Tools`}{' '}
                  <FiChevronDown
                    size={14}
                    className={!toolsDropdownOpen ? styles['rotated'] : ''}
                  />
                </button>
                {toolsDropdownOpen && (
                  <ul className={styles['dropdown-menu']}>
                    {tools.map(tool => (
                      <li
                        key={tool}
                        className={
                          selectedTools.includes(tool)
                            ? `${styles['dropdown-menu-item']} ${styles.selected}`
                            : styles['dropdown-menu-item']
                        }
                        onClick={() => toggleToolSelection(tool)}
                      >
                        <span className={styles['menu-item-text']}>{tool}</span>
                        <div className={styles.tooltip}>
                          {toolDescriptions[tool] || tool}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {mode === 'assistant' && (
              <div className={styles['tools-dropdown']} ref={assistantsRef}>
                <button
                  type="button"
                  className={styles['tools-button']}
                  onClick={toggleAssistantsDropdown}
                  data-tooltip={assistantsDropdownOpen ? undefined : "Select documentation sources"}
                >
                  {selectedAssistants.length === 0
                    ? 'Solutions'
                    : selectedAssistants.length === 1
                      ? getAssistantDisplay(selectedAssistants[0])
                      : `${selectedAssistants.length} Solutions`}{' '}
                  <FiChevronDown
                    size={14}
                    className={!assistantsDropdownOpen ? styles['rotated'] : ''}
                  />
                </button>
                {assistantsDropdownOpen && (
                  <ul className={styles['dropdown-menu']} style={{ whiteSpace: 'nowrap' }}>
                    {assistants.map(item => (
                      <li
                        key={item}
                        className={
                          selectedAssistants.includes(item)
                            ? `${styles['dropdown-menu-item']} ${styles.selected}`
                            : styles['dropdown-menu-item']
                        }
                        onClick={() => toggleAssistantSelection(item)}
                      >
                        <span className={styles['menu-item-text']}>
                          {getAssistantDisplay(item)}
                        </span>
                        <div className={styles.tooltip}>
                          Search {getAssistantDisplay(item)} knowledge base for official documentation, guides and best practices
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {mode === 'chat' && (
              <div className={styles['tools-dropdown']} ref={llmRef}>
                <button
                  type="button"
                  className={styles['tools-button']}
                  onClick={toggleLlmDropdown}
                  data-tooltip={llmDropdownOpen ? undefined : "Select language model"}
                >
                  {selectedLLM || 'LLMs'}{' '}
                  <FiChevronDown
                    size={14}
                    className={!llmDropdownOpen ? styles['rotated'] : ''}
                  />
                </button>
                {llmDropdownOpen && (
                  <ul className={styles['dropdown-menu']}
                      style={{ whiteSpace: 'nowrap' }}>
                    {llms.map(llm => (
                      <li
                        key={llm}
                        className={
                          selectedLLM === llm ? `${styles['dropdown-menu-item']} ${styles.selected}` : styles['dropdown-menu-item']
                        }
                        onClick={() => handleLLMSelect(llm)}
                      >
                        <span className={styles['menu-item-text']}>{llm}</span>
                        <div className={styles.tooltip}>
                          {llmDescriptions[llm]}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <div className={styles['action-buttons-right']}>
            <button
              type="button"
              className={`${styles['voice-button']} ${isRecording ? styles['recording'] : ''}`}
              onClick={handleVoiceClick}
              disabled={isTranscribing}
              aria-label={isRecording ? 'Stop recording' : isTranscribing ? 'Transcribing...' : 'Start voice input'}
              data-tooltip={isRecording ? undefined : isTranscribing ? undefined : 'Voice input'}
            >
              {isTranscribing ? (
                <ImSpinner8 size={16} className={styles['spinner']} />
              ) : isRecording ? (
                <>
                  <span className={styles['recording-indicator']} />
                  <span className={styles['recording-duration']}>{formatDuration(recordingDuration)}</span>
                  <FaStop size={12} />
                </>
              ) : (
                <FaMicrophone size={16} />
              )}
            </button>
            <button
              type="submit"
              className={styles['send-button']}
              aria-label="Send message"
              disabled={isTooLarge || !message.trim() || !assistantSelected}
            >
              <FiSend size={22} />
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ChatInput;
